import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { apiCall } from '../utils/api'

export default function UserProfileDropdown({ user, logout }) {
  const [isOpen, setIsOpen] = useState(false)
  const [profilePicture, setProfilePicture] = useState(null)
  const [imageError, setImageError] = useState(false)
  const dropdownRef = useRef(null)

  // Get user info from user object or localStorage
  const userName = user?.username || user?.name || user?.email?.split('@')[0] || 'User'
  const userEmail = user?.email || ''

  // Fetch profile picture from API if not in user object
  useEffect(() => {
    const fetchProfilePicture = async () => {
      // Check if picture is already in user object
      if (user?.picture) {
        setProfilePicture(user.picture)
        return
      }

      // Try to fetch from API
      try {
        const response = await apiCall('/v1/user/me')
        if (response.ok) {
          const userData = await response.json()
          if (userData?.picture) {
            setProfilePicture(userData.picture)
          }
        }
      } catch (error) {
        console.log('Could not fetch profile picture:', error)
      }
    }

    fetchProfilePicture()
  }, [user])

  // Generate avatar background color based on email
  const getAvatarColor = () => {
    const colors = ['bg-primary-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500', 'bg-pink-500', 'bg-primary-500']
    const index = userEmail.charCodeAt(0) % colors.length
    return colors[index]
  }

  // Get first letter for avatar
  const getInitial = () => {
    return userName.charAt(0).toUpperCase()
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLogout = () => {
    setIsOpen(false)
    logout()
  }

  const handleNavClick = () => {
    setIsOpen(false)
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-full"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Avatar - Show image if available and not errored */}
        {profilePicture && !imageError ? (
          <img
            src={profilePicture}
            alt={userName}
            className="w-9 h-9 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
            onError={() => setImageError(true)}
          />
        ) : (
          // Fallback Initial Avatar
          <div
            className={`${getAvatarColor()} w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm border-2 border-gray-200 dark:border-gray-700`}
          >
            {getInitial()}
          </div>
        )}

        {/* Dropdown Chevron */}
        <i className={`fas fa-chevron-down text-xs text-gray-600 dark:text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 py-1 z-50 animate-fade-in">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {userName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {userEmail}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <Link
              to="/dashboard"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={handleNavClick}
            >
              <i className="fas fa-chart-line w-5 text-primary-600 dark:text-primary-400"></i>
              <span className="ml-2">Dashboard</span>
            </Link>

            <Link
              to="/profile"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={handleNavClick}
            >
              <i className="fas fa-user w-5 text-primary-600 dark:text-primary-400"></i>
              <span className="ml-2">Profile</span>
            </Link>

            <Link
              to="/profile#security-api-keys"
              className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              onClick={() => setIsOpen(false)}
            >
              <i className="fas fa-key w-5 text-primary-600 dark:text-primary-400"></i>
              <span className="ml-2">API Keys</span>
            </Link>
          </div>

          {/* Logout Button */}
          <div className="border-t border-gray-200 dark:border-gray-700 py-1">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <i className="fas fa-sign-out-alt w-5"></i>
              <span className="ml-2">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
